import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile, getCachedProfile, reconnectRelays } from '../hooks/useNostrQuery';
import { useFollows, useFollowMutation } from '../hooks/useFollows';
import { getLocalEvents, useNostrPublish, getReadRelays } from '../hooks/useNostrPublish';
import { isSpamContent, isSpamAccount } from '../lib/bchFilter';
import { isBanned } from '../lib/communityFlag';
import { BOOST_KIND, BOOST_TAG, BOOST_SLOT_POSITIONS, BOOST_MAX_VISIBLE, isBoostActive, parseBoostData, sortBoostsByBid, type BoostData } from '../lib/boost';
import { CAMPAIGN_KIND, CAMPAIGN_TAG, type CampaignData } from '../lib/crowdfund';
import { calculateTrendingScore, getTrendingReasonLabel, getTrendingReasonEmoji, type TrendingScore } from '../lib/trending';
import { useBCHPrice, getBCHPrice } from '../hooks/useBCHPrice';
import { useNip05Verified } from '../hooks/useNip05Verified';
import { isVerifiedPubkeyCached, triggerVerificationForPubkeys } from '../lib/nip05verify';
import { saveVisibleCount, getSavedVisibleCount, saveFeedType, getSavedFeedType } from '../App';
import Composer from '../components/Composer';
import PostCard from '../components/PostCard';
import { Users, RefreshCw, Globe, Target, Clock, User, TrendingUp, Flame, X, Rocket, Heart, BadgeCheck, UserPlus, ChevronUp, Shield, Coins, Zap, ArrowRight, MessageCircle, FileText, Mic2, Radio, CheckCircle, QrCode, Music, Play, Pause, Disc3, Store } from 'lucide-react';
import { BCHLogo } from '../components/Layout';
import { isDataSaverEnabled } from '../lib/platform';
import { getRecruiterBadge } from '../lib/badges';
import { STREAM_EVENT_KIND, parseStreamEvent } from '../lib/streamCredentials';
import { useRooms, type LiveRoom } from '../hooks/useRooms';
import NewAccountBanner from '../components/NewAccountBanner';
import EnablePushBanner from '../components/EnablePushBanner';
import BCHLaunchCard from '../components/BCHLaunchCard';
import TrendingTokensCard from '../components/TrendingTokensCard';
import { useRadioPlayer } from '../contexts/RadioPlayerContext';
import BCHTipButton from '../components/BCHTipButton';
import SupportSomeoneNew from '../components/SupportSomeoneNew';
import { useBusinessAds } from '../hooks/useBusinessAds';
import SponsoredBusinessCard from '../components/SponsoredBusinessCard';

// Feed positions (0-indexed, counting rendered items) after which a Sponsored
// business card is injected. Deliberately offset from BOOST_SLOT_POSITIONS
// ([2,5,9,14,20]) so post-boosts and business ads never land on the same row.
const BUSINESS_AD_SLOTS = [4, 11, 18];

const POSTS_PER_PAGE = 20;
const INVITE_DISMISSED_KEY = 'bchnostr:invite-banner-dismissed';
const SCAM_WARNING_DISMISSED_KEY = 'bchnostr:scam-warning-dismissed';
const GROWTH_SINCE = Math.floor(new Date('2025-01-01').getTime() / 1000);

// ══════════════════════════════════════════════════════════════════
//  BACKGROUND USER COUNTER
//
//  The old approach used two useNostrQuery calls with high limits
//  (5000 / 3000+2000), but relays return the most RECENT N events.
//  As the community grows, older users' events fall outside the limit
//  window and the count gets stuck.
//
//  This counter solves it by:
//    1. Splitting the time range into overlapping windows so we page
//       through ALL events, not just the most recent batch.
//    2. Running as a background singleton (once per session) so it
//       doesn't block rendering or create duplicate subscriptions.
//    3. Accumulating pubkeys in a persistent Set — the count can
//       only grow, never shrink within a session.
//    4. Persisting the high-water mark and the actual pubkey set to
//       localStorage so it survives page reloads.
// ══════════════════════════════════════════════════════════════════

const USER_COUNT_LS_KEY = 'bchnostr:user-high-count';
const USER_PUBKEYS_LS_KEY = 'bchnostr:user-pubkeys';
const USER_COUNTER_VERSION = 4; // v4: fix cache clear bug — old high-water mark was never cleared on version bump
const USER_COUNTER_VER_KEY = 'bchnostr:user-counter-ver';

// Module-level singleton state
let _userCounterStarted = false;
let _userCounterListeners: Array<(count: number) => void> = [];
let _allPubkeys = new Set<string>();
let _currentCount = 0;

function _initPubkeysFromStorage() {
  try {
    // If version mismatch, clear ALL stale cached data — both the pubkey
    // set AND the high-water count. Without clearing both, the old inflated
    // count persists via Math.max in _notifyListeners.
    const ver = parseInt(localStorage.getItem(USER_COUNTER_VER_KEY) || '0') || 0;
    if (ver < USER_COUNTER_VERSION) {
      localStorage.removeItem(USER_PUBKEYS_LS_KEY);
      localStorage.removeItem(USER_COUNT_LS_KEY);
      localStorage.setItem(USER_COUNTER_VER_KEY, String(USER_COUNTER_VERSION));
      _allPubkeys.clear();
      _currentCount = 0;
      return; // Start fresh — the counter will populate from relay queries
    }

    const stored = localStorage.getItem(USER_PUBKEYS_LS_KEY);
    if (stored) {
      const arr = JSON.parse(stored) as string[];
      if (Array.isArray(arr)) {
        arr.forEach(pk => _allPubkeys.add(pk));
      }
    }
    _currentCount = _allPubkeys.size;
  } catch {}
}

function _persistPubkeys() {
  try {
    // Only store the pubkey list if it's manageable size (< 200KB)
    if (_allPubkeys.size < 5000) {
      localStorage.setItem(USER_PUBKEYS_LS_KEY, JSON.stringify([..._allPubkeys]));
    }
    localStorage.setItem(USER_COUNT_LS_KEY, String(_currentCount));
    localStorage.setItem(USER_COUNTER_VER_KEY, String(USER_COUNTER_VERSION));
  } catch {}
}

function _notifyListeners() {
  const newCount = _allPubkeys.size;
  if (newCount !== _currentCount) {
    _currentCount = newCount;
    _persistPubkeys();
  }
  _userCounterListeners.forEach(fn => fn(_currentCount));
}

function _startUserCounter() {
  if (_userCounterStarted) return;
  _userCounterStarted = true;
  _initPubkeysFromStorage();
  _notifyListeners();

  // Use the same relays as the app
  const relays = getReadRelays();

  // Build time windows — 3-month chunks from GROWTH_SINCE to now
  const now = Math.floor(Date.now() / 1000);
  const WINDOW_SIZE = 90 * 24 * 3600; // 90 days
  const windows: Array<{ since: number; until: number }> = [];
  let t = GROWTH_SINCE;
  while (t < now) {
    const end = Math.min(t + WINDOW_SIZE, now);
    windows.push({ since: t, until: end });
    t = end;
  }

  // Kinds that carry the client tag
  const clientKinds = [1, 22, 21, 1068, 1111, 30023, 30024, 30100, 30311, 30312, 30402, 34235, 34236, 34550];

  // For each relay, send all time-windowed queries in parallel
  // Each window gets its own limit so we effectively multiply the coverage
  const LIMIT_PER_WINDOW = 5000;

  for (const url of relays) {
    try {
      const ws = new WebSocket(url);
      const subIds = new Set<string>();
      let eoseCount = 0;
      const totalSubs = windows.length; // 1 filter group per window (client-tagged only)
      const timeout = setTimeout(() => { try { ws.close(); } catch {} }, 30000);

      ws.onopen = () => {
        for (let wi = 0; wi < windows.length; wi++) {
          const w = windows[wi];

          // Only count client-tagged events — these are actual BCH Nostr users.
          // The old approach also counted #bch and #bitcoincash tagged posts from
          // ANY Nostr app, which inflated the count significantly.
          const subA = `uc_a_${wi}_${Math.random().toString(36).slice(2, 6)}`;
          subIds.add(subA);
          ws.send(JSON.stringify(['REQ', subA,
            { kinds: clientKinds, '#client': ['BCHNostr'], limit: LIMIT_PER_WINDOW, since: w.since, until: w.until },
          ]));
        }
      };

      ws.onmessage = (msg) => {
        try {
          const data = JSON.parse(msg.data);
          if (!Array.isArray(data)) return;

          if (data[0] === 'EVENT' && subIds.has(data[1]) && data[2]?.pubkey) {
            _allPubkeys.add(data[2].pubkey);
          }

          if (data[0] === 'EOSE' && subIds.has(data[1])) {
            eoseCount++;
            // Notify after each EOSE so count updates progressively
            _notifyListeners();
            // Close sub after EOSE
            try { ws.send(JSON.stringify(['CLOSE', data[1]])); } catch {}

            if (eoseCount >= totalSubs) {
              clearTimeout(timeout);
              try { ws.close(); } catch {}
            }
          }
        } catch {}
      };

      ws.onerror = () => { clearTimeout(timeout); };
      ws.onclose = () => { clearTimeout(timeout); _notifyListeners(); };
    } catch {}
  }
}

/** React hook that returns the live user count */
function useUserCount(): number {
  const [count, setCount] = useState(() => {
    _initPubkeysFromStorage();
    return _currentCount;
  });

  useEffect(() => {
    const listener = (c: number) => setCount(c);
    _userCounterListeners.push(listener);

    // Start the background counter on first subscriber
    _startUserCounter();

    return () => {
      _userCounterListeners = _userCounterListeners.filter(fn => fn !== listener);
    };
  }, []);

  return count;
}

// ── Live Stream Banner ────────────────────────────────────────────
// Shows a compact notification on the main feed when someone is
// streaming live. Queries kind 30311 events tagged with BCH.
const LIVE_BANNER_DISMISSED_KEY = 'bchnostr:live-banner-dismissed';

function LiveStreamBanner() {
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 3600 * 2);
  const { events: streamEvents, refetch: refetchStreams } = useNostrQuery({
    filters: [
      { kinds: [STREAM_EVENT_KIND], '#t': ['bch-live'], limit: 20, since },
      { kinds: [STREAM_EVENT_KIND], '#t': ['bch'], limit: 20, since },
      { kinds: [STREAM_EVENT_KIND], '#t': ['bitcoincash'], limit: 20, since },
    ],
    allRelays: true,
  });

  // Poll for stream status changes every 30s so "ended" events are picked up
  useEffect(() => {
    const i = setInterval(refetchStreams, 30000);
    return () => clearInterval(i);
  }, [refetchStreams]);

  // Find live streams — deduplicate by pubkey:d-tag
  const liveStreams = useMemo(() => {
    const byKey = new Map<string, any>();
    for (const ev of streamEvents) {
      const d = ev.tags?.find((t: string[]) => t[0] === 'd')?.[1];
      if (!d) continue;
      const key = `${ev.pubkey}:${d}`;
      const existing = byKey.get(key);
      if (!existing || ev.created_at > existing.created_at) byKey.set(key, ev);
    }
    return Array.from(byKey.values())
      .map(ev => parseStreamEvent(ev))
      .filter(s => s.isLive);
  }, [streamEvents]);

  // Track which stream IDs were dismissed this session
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(LIVE_BANNER_DISMISSED_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  const visibleStreams = liveStreams.filter(s => !dismissedIds.has(s.id));

  if (visibleStreams.length === 0) return null;

  const handleDismiss = (streamId: string) => {
    const next = new Set(dismissedIds);
    next.add(streamId);
    setDismissedIds(next);
    try { sessionStorage.setItem(LIVE_BANNER_DISMISSED_KEY, JSON.stringify([...next])); } catch {}
  };

  return (
    <>
      {visibleStreams.map(stream => (
        <LiveStreamBannerItem key={stream.id} stream={stream} onDismiss={() => handleDismiss(stream.id)} />
      ))}
    </>
  );
}

function LiveStreamBannerItem({ stream, onDismiss }: { stream: any; onDismiss: () => void }) {
  const { profile } = useNostrProfile(stream.pubkey);
  const name = profile?.display_name || profile?.name || stream.pubkey.slice(0, 8) + '…';

  return (
    <div className="rounded-2xl border border-red-500/30 bg-gradient-to-r from-red-500/5 via-red-500/10 to-red-500/5 p-3 relative overflow-hidden">
      {/* Dismiss */}
      <button
        onClick={onDismiss}
        className="absolute top-2.5 right-2.5 p-1 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-foreground transition-colors z-10"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <Link to="/live" className="flex items-center gap-3 group">
        {/* Pulsing live indicator */}
        <div className="relative shrink-0">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-red-500/50" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center ring-2 ring-red-500/50">
              <Radio className="w-5 h-5 text-red-500" />
            </div>
          )}
          <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center border-2 border-background">
            <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
          </div>
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-500 text-white text-[9px] font-bold uppercase tracking-wider">
              <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live
            </span>
            <span className="text-xs font-semibold truncate">{name}</span>
          </div>
          <p className="text-[11px] text-muted-foreground truncate mt-0.5">
            {stream.title || 'BCH Live Stream'}
          </p>
          <p className="text-[10px] text-red-500 font-medium mt-0.5 group-hover:underline">
            Watch now →
          </p>
        </div>
      </Link>
    </div>
  );
}

// ── Live BCH Rooms Banner ─────────────────────────────────────────
// Compact notification when a native BCH Room has people in it. A room
// counts as live from NIP-53 presence, so this never advertises an empty
// room. Same dismiss-per-session pattern as streams.
const ROOMS_BANNER_DISMISSED_KEY = 'bchnostr:rooms-banner-dismissed';

function LiveRoomsBanner() {
  const { liveRooms } = useRooms();

  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(ROOMS_BANNER_DISMISSED_KEY);
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });

  // Show the busiest live room (most participants); it's sorted first.
  const room = liveRooms.find(r => !dismissedIds.has(r.coordinate));
  if (!room) return null;

  const handleDismiss = () => {
    const next = new Set(dismissedIds);
    next.add(room.coordinate);
    setDismissedIds(next);
    try { sessionStorage.setItem(ROOMS_BANNER_DISMISSED_KEY, JSON.stringify([...next])); } catch {}
  };

  return <LiveRoomsBannerItem room={room} liveCount={liveRooms.length} onDismiss={handleDismiss} />;
}

function LiveRoomsBannerItem({ room, liveCount, onDismiss }: { room: LiveRoom; liveCount: number; onDismiss: () => void }) {
  const { profile } = useNostrProfile(room.host);
  const hostName = profile?.display_name || profile?.name || room.host.slice(0, 8) + '…';

  return (
    <div className="rounded-2xl border border-primary/30 bg-gradient-to-r from-primary/5 via-primary/10 to-primary/5 p-3 relative overflow-hidden">
      <button
        onClick={onDismiss}
        className="absolute top-2.5 right-2.5 p-1 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-foreground transition-colors z-10"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <Link to={`/rooms?room=${encodeURIComponent(room.coordinate)}`} className="flex items-center gap-3 group">
        <div className="relative shrink-0">
          <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-primary/50 bg-primary/10 flex items-center justify-center">
            {room.image
              ? <img src={room.image} alt="" className="w-full h-full object-cover" />
              : <Mic2 className="w-4 h-4 text-primary" />}
          </div>
          <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary flex items-center justify-center border-2 border-background">
            <Mic2 className="w-2 h-2 text-primary-foreground" />
          </div>
        </div>

        <div className="flex-1 min-w-0 pr-6">
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-primary text-primary-foreground text-[9px] font-bold uppercase tracking-wider">
              <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> Live Audio
            </span>
            {liveCount > 1 && (
              <span className="text-[10px] text-muted-foreground">+{liveCount - 1} more</span>
            )}
          </div>
          <p className="text-xs font-semibold truncate mt-0.5">{room.title}</p>
          <p className="text-[11px] text-muted-foreground truncate">
            {hostName}
            {room.participants > 0 && ` · ${room.participants} listening`}
          </p>
          <p className="text-[10px] text-primary font-medium mt-0.5 group-hover:underline">
            Join the room →
          </p>
        </div>
      </Link>
    </div>
  );
}

// ── Scam / Bot Warning Banner ─────────────────────────────────────
function ScamWarningBanner() {
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(SCAM_WARNING_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(SCAM_WARNING_DISMISSED_KEY, '1'); } catch {}
  };

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 relative">
      <button onClick={handleDismiss} className="absolute top-3 right-3 p-1 rounded-lg hover:bg-amber-500/10 text-muted-foreground hover:text-foreground transition-colors" title="Dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
      <div className="flex gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
          <Shield className="w-5 h-5 text-amber-500" />
        </div>
        <div className="space-y-1.5 pr-6">
          <h3 className="text-sm font-bold text-amber-600 dark:text-amber-400">Watch out for scammers &amp; bots</h3>
          <p className="text-xs text-muted-foreground leading-relaxed">
            Nostr is an open network — anyone can post. Be cautious of accounts offering free crypto, asking for your keys, or sending suspicious links. <strong className="text-foreground">Never share your private key (nsec).</strong>
          </p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
            <p className="text-[10px] text-muted-foreground flex items-center gap-1"><BadgeCheck className="w-3 h-3 text-primary" /> Look for <span className="text-primary font-medium">@bchnostr.com</span> verified users</p>
            <p className="text-[10px] text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3 text-amber-500" /> Report &amp; mute suspicious accounts</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Hero Splash for Logged-Out Visitors ───────────────────────────
// Shows a compelling landing section above the feed for first-time visitors.
// Dismissed once they log in or tap "Browse feed".

const HERO_DISMISSED_KEY = 'bchnostr:hero-dismissed';

const HeroSplash = React.memo(function HeroSplash({ onLogin, userCount }: { onLogin: () => void; userCount: number }) {
  const [dismissed, setDismissed] = React.useState(() => {
    try { return sessionStorage.getItem(HERO_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  if (dismissed) return null;

  const handleDismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(HERO_DISMISSED_KEY, '1'); } catch {}
  };

  return (
    <div className="relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/8 via-primary/3 to-transparent dark:from-primary/12 dark:via-primary/5 dark:to-transparent" />
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-emerald-500/5 blur-3xl" />
      </div>

      <div className="relative px-4 pt-8 pb-6 max-w-lg mx-auto">
        {/* Logo + Title */}
        <div className="text-center mb-6">
          <div className="relative inline-block mb-4">
            <img
              src="/icons/icon-512x512.png"
              alt="BCHNostr"
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl shadow-lg shadow-primary/20 bch-pulse"
            />
            <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-background rounded-full flex items-center justify-center border-2 border-primary shadow-sm">
              <Zap className="w-3.5 h-3.5 text-primary" />
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-bch-gradient leading-tight">
            BCHNostr
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground font-medium mt-1">
            Bitcoin Cash meets Social
          </p>
        </div>

        {/* Value props */}
        <div className="grid gap-2.5 mb-6">
          {[
            {
              icon: <Coins className="w-4.5 h-4.5 text-emerald-500" />,
              title: 'Tip with real BCH',
              desc: 'Send Bitcoin Cash to anyone with one click. Real money, not points.',
              color: 'border-emerald-500/20 bg-emerald-500/5',
            },
            {
              icon: <Shield className="w-4.5 h-4.5 text-primary" />,
              title: 'Own your identity',
              desc: 'No email, no phone number. Your keys, your account. Forever.',
              color: 'border-primary/20 bg-primary/5',
            },
            {
              icon: <Users className="w-4.5 h-4.5 text-amber-500" />,
              title: 'No middleman',
              desc: 'Decentralized on Nostr. No company can ban you or delete your posts.',
              color: 'border-amber-500/20 bg-amber-500/5',
            },
          ].map(item => (
            <div key={item.title} className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${item.color}`}>
              <div className="mt-0.5 shrink-0">{item.icon}</div>
              <div>
                <p className="text-sm font-bold text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Feature pills */}
        <div className="flex flex-wrap justify-center gap-1.5 mb-6">
          {[
            { icon: <MessageCircle className="w-3 h-3" />, label: 'Social Feed' },
            { icon: <FileText className="w-3 h-3" />, label: 'BCH Ink' },
            { icon: <Target className="w-3 h-3" />, label: 'Crowdfund' },
            { icon: <Mic2 className="w-3 h-3" />, label: 'BCH Rooms' },
            { icon: <Heart className="w-3 h-3" />, label: 'BCH Tips' },
            { icon: <Radio className="w-3 h-3" />, label: 'Dedicated Relay' },
          ].map(f => (
            <span key={f.label} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold border border-border bg-card text-muted-foreground">
              {f.icon} {f.label}
            </span>
          ))}
        </div>

        {/* Social proof */}
        {userCount > 0 && (
          <p className="text-center text-xs text-muted-foreground mb-4">
            <span className="font-bold text-primary">{userCount.toLocaleString()}</span> people already posting with BCH
          </p>
        )}

        {/* CTA Buttons */}
        <div className="space-y-2.5">
          <button
            onClick={() => {
              (window as any).__bchnostr_signup = true;
              (window as any).__bchnostr_signup_only = true;
              onLogin();
            }}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white font-bold text-sm shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:opacity-95 active:scale-[0.98] transition-all"
          >
            <UserPlus className="w-4.5 h-4.5" />
            Join BCHNostr — It's Free
          </button>
          <button
            onClick={() => {
              // A Business Page must be owned by a personal account, so we
              // sign the owner up first, then auto-open the Create Business
              // Page flow (handled in Layout once they're logged in).
              //
              // Login triggers a full page reload (useCurrentUser.finishLogin),
              // so the intent must survive in localStorage — a window flag
              // would be wiped by the reload.
              (window as any).__bchnostr_signup = true;
              (window as any).__bchnostr_signup_only = true;
              try { localStorage.setItem('bchnostr:after-signup-business', '1'); } catch { /* ignore */ }
              onLogin();
            }}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-primary/30 bg-primary/5 text-primary font-semibold text-sm hover:bg-primary/10 transition-colors"
          >
            <Store className="w-4 h-4" />
            Create a Business Account
          </button>
          <button
            onClick={onLogin}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-border bg-card text-foreground font-semibold text-sm hover:bg-muted transition-colors"
          >
            Already have a Nostr account? Log in
          </button>
          <button
            onClick={handleDismiss}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Browse the feed first <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Separator */}
      <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
    </div>
  );
});

const VERIFIED_DISMISSED_KEY = 'bchnostr:verified-banner-dismissed';

/** Slim CTA to get verified — only shown for logged-in users without @bchnostr.com NIP-05 */
function GetVerifiedBanner({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  const nip05 = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  const isVerified = useNip05Verified(nip05, pubkey);
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(VERIFIED_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  // Don't render if actually verified (server-confirmed) or dismissed
  if (dismissed || isVerified) return null;

  return (
    <div className="mx-3 mb-2 flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-2.5" style={{ minHeight: 52 }}>
      <BadgeCheck className="w-5 h-5 text-emerald-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">Get verified on BCH Nostr</p>
        <p className="text-xs text-muted-foreground">Claim <strong>yourname@bchnostr.com</strong> — just $1 in BCH</p>
      </div>
      <Link
        to="/settings?tab=profile"
        className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 transition-colors"
      >
        Get verified
      </Link>
      <button
        onClick={() => { try { localStorage.setItem(VERIFIED_DISMISSED_KEY, '1'); } catch {} setDismissed(true); }}
        className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

const RELAY_BANNER_DISMISSED_KEY = 'bchnostr:relay-banner-dismissed';

/** CTA to join the BCH Nostr relay — shown for logged-in users who haven't
 *  added relay.bchnostr.com to their relay list yet. Links to the relay
 *  website where they can request whitelisting. */
function JoinRelayBanner() {
  const [dismissed, setDismissed] = React.useState(() => {
    try { return localStorage.getItem(RELAY_BANNER_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  // Check if user already has the relay configured
  const hasRelay = React.useMemo(() => {
    try {
      const cfg = JSON.parse(localStorage.getItem('nostr:app-config') || '{}');
      const relays: { url: string }[] = cfg.relayMetadata?.relays || [];
      return relays.some(r => r.url.includes('relay.bchnostr.com'));
    } catch { return false; }
  }, []);

  if (dismissed || hasRelay) return null;

  return (
    <div className="mx-3 mb-2 flex items-center gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-2.5" style={{ minHeight: 52 }}>
      <Radio className="w-5 h-5 text-primary shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">Join the BCH Nostr Relay</p>
        <p className="text-xs text-muted-foreground">Faster posts &amp; a dedicated BCH community relay</p>
      </div>
      <a
        href="https://relay.bchnostr.com"
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-colors"
      >
        Join relay
      </a>
      <button
        onClick={() => { try { localStorage.setItem(RELAY_BANNER_DISMISSED_KEY, '1'); } catch {} setDismissed(true); }}
        className="text-muted-foreground hover:text-foreground shrink-0 p-0.5"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

/** Live countdown showing time remaining on a boost.
 *  Uses a self-contained ref + DOM update to avoid triggering parent re-renders
 *  every 60 seconds. This is critical for feed stability — a setState call here
 *  would cascade up through the boost wrapper and potentially re-render the entire feed.
 */
function BoostCountdown({ expiresAt, on = 'gold' }: { expiresAt?: number; on?: 'gold' | 'plain' }) {
  const spanRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!expiresAt) return;
    const update = () => {
      const remaining = expiresAt - Math.floor(Date.now() / 1000);
      if (remaining <= 0) {
        if (spanRef.current) spanRef.current.textContent = '';
        return;
      }
      const hours = Math.floor(remaining / 3600);
      const mins = Math.floor((remaining % 3600) / 60);
      const label = hours > 0 ? `${hours}h ${mins}m left` : `${mins}m left`;
      if (spanRef.current) spanRef.current.textContent = label;
    };
    update();
    // Tick every 10s so the minute value never looks stale by up to a minute
    const interval = setInterval(update, 10000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt) return null;

  return (
    <span
      ref={spanRef}
      // aria-live so screen readers aren't spammed, but the value is still exposed
      aria-live="off"
      className={`boost-countdown ml-auto shrink-0 ${
        on === 'gold' ? 'boost-countdown-on-gold' : 'boost-countdown-plain'
      }`}
    />
  );
}

function satsToUSD(sats: number, bchPrice: number | null): string | null {
  if (!bchPrice || bchPrice <= 0) return null;
  const usd = (sats / 1e8) * bchPrice;
  if (usd < 0.01) return '<$0.01';
  return '$' + usd.toFixed(2);
}

const WhoToFollowWidget = React.memo(function WhoToFollowWidget({ myPubkey, follows, communityEvents }: {
  myPubkey?: string;
  follows: string[];
  communityEvents: import('nostr-tools').Event[];
}) {
  const [dismissed, setDismissed] = React.useState(() => {
    try { return sessionStorage.getItem('bchnostr:wtf-dismissed') === '1'; } catch { return false; }
  });

  // Derive suggestions from most active community posters
  // Must be before any early returns to respect rules of hooks
  const suggestions = React.useMemo(() => {
    if (!myPubkey || !communityEvents) return [];
    const postCount: Record<string, number> = {};
    communityEvents.forEach(ev => {
      if (ev.pubkey === myPubkey) return;
      if (follows.includes(ev.pubkey)) return;
      postCount[ev.pubkey] = (postCount[ev.pubkey] || 0) + 1;
    });
    return Object.entries(postCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 4)
      .map(([pk]) => pk);
  }, [communityEvents, myPubkey, follows]);

  // Only show to logged-in users with fewer than 10 follows
  if (!myPubkey || follows.length >= 10 || dismissed) return null;
  if (suggestions.length === 0) return null;

  return (
    <div className="mx-3 my-2 rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-border/60">
        <h3 className="text-sm font-bold flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-primary" /> Who to Follow
        </h3>
        <button
          onClick={() => { setDismissed(true); try { sessionStorage.setItem('bchnostr:wtf-dismissed', '1'); } catch {} }}
          className="text-muted-foreground hover:text-foreground p-0.5"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="divide-y divide-border/60">
        {suggestions.slice(0, 4).map(pk => (
          <SuggestedUser key={pk} pubkey={pk} myPubkey={myPubkey} currentFollows={follows} />
        ))}
      </div>
      <div className="px-4 py-2 border-t border-border/60">
        <p className="text-[11px] text-muted-foreground">
          Follow people to build your personalized feed →{' '}
          <button className="text-primary hover:underline font-medium" onClick={() => { setDismissed(true); try { sessionStorage.setItem('bchnostr:wtf-dismissed', '1'); } catch {} }}>
            Got it
          </button>
        </p>
      </div>
    </div>
  );
});

function SuggestedUser({ pubkey, myPubkey, currentFollows }: { pubkey: string; myPubkey: string; currentFollows: string[] }) {
  const { profile } = useNostrProfile(pubkey);
  const { mutate: followMutate, ready: followsReady } = useFollowMutation();
  const [followed, setFollowed] = React.useState(false);
  const [following, setFollowing] = React.useState(false);

  const isAlreadyFollowing = followed || currentFollows.includes(pubkey);

  const handleFollow = async () => {
    if (isAlreadyFollowing || following || !followsReady) return;
    setFollowing(true);
    try {
      const success = await followMutate('follow', pubkey);
      if (success) setFollowed(true);
    } catch {}
    setFollowing(false);
  };

  if (!profile && !pubkey) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-2.5">
      <Link to={`/profile/${pubkey}`} className="shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-4 h-4 text-primary/50" />
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/profile/${pubkey}`} className="text-sm font-semibold hover:text-primary transition-colors truncate block">
          {profile?.display_name || profile?.name || pubkey.slice(0, 10) + '...'}
        </Link>
        {profile?.about && (
          <p className="text-[11px] text-muted-foreground truncate">{profile.about}</p>
        )}
      </div>
      <button
        onClick={handleFollow}
        disabled={isAlreadyFollowing || following}
        className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
          isAlreadyFollowing
            ? 'bg-muted text-muted-foreground cursor-default'
            : 'bg-primary text-primary-foreground hover:opacity-90 active:scale-95'
        }`}
      >
        {following ? '...' : isAlreadyFollowing ? 'Following' : 'Follow'}
      </button>
    </div>
  );
}

// ── New Posts Banner ──────────────────────────────────────────────
const NewPostsBanner = React.memo(function NewPostsBanner({ count, onShow }: { count: number; onShow: () => void }) {
  if (count === 0) return null;
  return (
    <button
      onClick={onShow}
      className="sticky top-[57px] z-20 w-full flex items-center justify-center gap-2 py-2 bg-primary text-primary-foreground text-xs font-semibold shadow-md shadow-primary/20 hover:bg-primary/90 active:scale-[0.99] transition-all"
    >
      <ChevronUp className="w-3.5 h-3.5" />
      {count} new post{count !== 1 ? 's' : ''} — tap to refresh
    </button>
  );
});

export default function Index() {
  const { user, isMuted, login } = useAppContext();
  const location = useLocation();
  // Restore feedType from session storage so returning via back button
  // lands on the same tab the user was viewing.
  const [feedType, setFeedType] = useState<'community' | 'following' | 'trending' | 'verified'>(() => {
    const saved = getSavedFeedType(location.pathname);
    if (saved === 'community' || saved === 'following' || saved === 'trending' || saved === 'verified') return saved;
    return 'community';
  });
  // Restore visibleCount from session storage so returning via back button
  // renders enough posts for scroll restoration to reach the saved position.
  const [visibleCount, setVisibleCount] = useState(() => {
    return getSavedVisibleCount(location.pathname) || POSTS_PER_PAGE;
  });

  // ── Scroll-stable "Load More" ─────────────────────────────────────
  // "Load More" appends older posts to the bottom. On some browsers
  // (notably Chrome/Samsung Internet) the combination of
  // `content-visibility: auto` + `overflow-anchor: none` on feed posts
  // caused the viewport to jump — posts appeared to insert *above* the
  // user's position, forcing a scroll back up. iOS Safari, which handles
  // `content-visibility` differently, didn't show the jump. To make the
  // behavior identical everywhere we explicitly capture the scroll offset
  // when Load More is pressed and restore it after the new posts render.
  const loadMoreScrollY = useRef<number | null>(null);
  const handleLoadMore = useCallback(() => {
    loadMoreScrollY.current = window.scrollY;
    setVisibleCount(prev => prev + POSTS_PER_PAGE);
  }, []);
  useLayoutEffect(() => {
    if (loadMoreScrollY.current != null) {
      const y = loadMoreScrollY.current;
      loadMoreScrollY.current = null;
      // Restore synchronously (before paint) so there is no visible jump.
      window.scrollTo(0, y);
    }
  }, [visibleCount]);
  const { follows } = useFollows(user?.pubkey);
  const { price: bchPrice } = useBCHPrice();

  // Stable since — rounded to the nearest hour so the filter JSON (and thus
  // the query cache key) is identical across component re-mounts within the
  // same hour. This is critical for scroll restoration: without it, every
  // re-mount produces a new `since` value → new cache key → cache miss →
  // empty feed → scroll restoration fails.
  const [since] = useState(() => {
    const now = Math.floor(Date.now() / 1000);
    return Math.floor((now - 3600 * 24 * 7) / 3600) * 3600; // round to hour
  });
  // 48h window for trending (hard cutoff), also rounded to hour
  const [trendingSince] = useState(() => {
    const now = Math.floor(Date.now() / 1000);
    return Math.floor((now - 3600 * 48) / 3600) * 3600;
  });

  // Community feed: BCH Nostr is about more than Bitcoin Cash. The feed shows
  // everything posted *from* BCH Nostr — any post carrying the `client:BCHNostr`
  // NIP-89 tag — regardless of whether it includes a BCH hashtag. We keep the
  // hashtag filters as well so BCH-tagged posts from *other* Nostr clients
  // (Damus, Amethyst, etc.) still surface here, and always include the
  // logged-in user's own posts so they never disappear.
  // Memoize so the filter JSON (cache key) is stable across re-renders.
  const [_dataSaver] = useState(isDataSaverEnabled);
  const feedLimit = _dataSaver ? 50 : 200;
  const smallLimit = _dataSaver ? 20 : 50;
  const communityFilter = useMemo(() => [
    // Posts made from within BCH Nostr — no BCH *topic* hashtag required.
    // Every post the app publishes now carries a `#bchnostr` tag (see
    // Composer), and unlike the `client` tag this is a single-letter `t`
    // tag that relays actually index and can filter on. This is the primary
    // community signal, so it gets the full feed limit.
    { kinds: [1, 1068], '#t': ['bchnostr'], limit: feedLimit, since },
    // BCH-tagged posts from any Nostr client, so the wider BCH conversation
    // still shows up even when it originates outside BCH Nostr.
    { kinds: [1, 1068], '#t': ['bch'], limit: feedLimit, since },
    { kinds: [1, 1068], '#t': ['bitcoincash'], limit: feedLimit, since },
    { kinds: [1, 1068], '#t': ['cashtokens'], limit: smallLimit, since },
    // Always fetch the logged-in user's own posts so they never disappear
    ...(user ? [{ kinds: [1, 1068], authors: [user.pubkey], limit: 30, since }] : []),
  ], [since, user?.pubkey, feedLimit, smallLimit]);

  // Following feed: posts from people you follow only.
  const followFilter = useMemo(() =>
    follows.length > 0
      ? [{ kinds: [1, 1068], authors: follows, limit: feedLimit, since }]
      : [],
    [follows, since, feedLimit],
  );

  // Reset visible count when switching tabs (but not on initial mount with restored feedType)
  const feedTypeInitRef = useRef(true);
  useEffect(() => {
    if (feedTypeInitRef.current) {
      feedTypeInitRef.current = false;
      return;
    }
    setVisibleCount(POSTS_PER_PAGE);
  }, [feedType]);

  // Persist visibleCount and feedType to sessionStorage so they survive navigation
  useEffect(() => {
    saveVisibleCount(location.pathname, visibleCount);
  }, [visibleCount, location.pathname]);
  useEffect(() => {
    saveFeedType(location.pathname, feedType);
  }, [feedType, location.pathname]);

  // Community events are always fetched (trending + verified depend on them)
  // Use allRelays for better coverage — posts may be on relays the user doesn't
  // have in their top 3, causing them to "disappear" from the feed.
  const { events: communityEvents, loading: communityLoading, refetch: refetchCommunity } = useNostrQuery({
    filters: communityFilter,
    enabled: feedType === 'community' || feedType === 'trending' || feedType === 'verified',
    allRelays: !_dataSaver,
  });

  const { events: followEvents, loading: followLoading, refetch: refetchFollow } = useNostrQuery({
    filters: followFilter,
    enabled: feedType === 'following' && follows.length > 0,
    allRelays: !_dataSaver,
  });

  // ── Count unique BCHNostr users ──────────────────────────────────
  // Uses a background paginated counter that queries multiple time
  // windows across all relays. This solves the relay limit problem:
  // a single query with limit:5000 only gets the most recent events,
  // missing older users. The paginated counter splits the time range
  // into 3-month windows so every user is counted.
  const userCount = useUserCount();

  // Fetch active boosts — use 48h window so boosts purchased yesterday
  // are still found even if they haven't expired yet (max tier is 24h).
  // Query all relays for reliability since boost events are critical.
  const [boostSince] = useState(() => {
    const now = Math.floor(Date.now() / 1000);
    return Math.floor((now - 86400 * 2) / 3600) * 3600; // round to hour
  });
  const { events: boostEvents, refetch: refetchBoosts } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [BOOST_TAG], since: boostSince, limit: _dataSaver ? 20 : 50 }],
    enabled: true,
    allRelays: !_dataSaver,
  });

  // Get active boost event IDs with their data (not expired).
  // A user can boost multiple different posts — all active boosts are shown.
  // For the same post (same eventId), only the latest boost version is kept.
  const activeBoostMap = useMemo(() => {
    const map = new Map<string, BoostData>();
    boostEvents.forEach(ev => {
      if (!isBoostActive(ev)) return;
      const data = parseBoostData(ev);
      if (!data) return;
      // For the same boosted post (eventId), keep the version with latest expiresAt
      const existing = map.get(data.eventId);
      if (!existing || data.expiresAt > existing.expiresAt) {
        map.set(data.eventId, data);
      }
    });

    // Diagnostic log — helps debug boost resolution issues
    if (boostEvents.length > 0) {
      const active = boostEvents.filter(e => isBoostActive(e)).length;
      const expired = boostEvents.length - active;
      console.log(
        `[Boost] ${boostEvents.length} boost events found (${active} active, ${expired} expired), ` +
        `${map.size} unique posts boosted: [${[...map.keys()].map(id => id.slice(0, 8) + '…').join(', ')}]`
      );
    }

    return map;
  }, [boostEvents]);
  const activeBoostedIds = useMemo(() => new Set(activeBoostMap.keys()), [activeBoostMap]);

  // ── Refresh boosts when a new boost is published ──────────────────
  // The BoostPostModal dispatches a custom event after publishing.
  // We listen for it and aggressively refetch boost data from relays
  // so the "Promoted" badge appears without requiring a manual page refresh.
  useEffect(() => {
    const handler = () => {
      // Cascade refetches to account for relay propagation delay
      refetchBoosts();
      setTimeout(refetchBoosts, 2000);
      setTimeout(refetchBoosts, 5000);
      setTimeout(refetchBoosts, 10000);
    };
    window.addEventListener('bchnostr:boost-published', handler);
    return () => window.removeEventListener('bchnostr:boost-published', handler);
  }, [refetchBoosts]);

  // ── Reconnect + refetch when the app returns to the foreground ────
  // Installed PWAs (and long-backgrounded mobile browsers) get suspended by
  // the OS, which silently kills every WebSocket. Unlike a fresh page load,
  // resuming the app does NOT re-open those sockets, so the relay pool sits
  // on dead handles and the cached feed shows *without* boosted posts — the
  // exact "boosts don't show in the PWA" symptom. On visibility/focus we
  // reconnect the relay pool and re-run the feed + boost queries so promoted
  // posts appear the same as in a fresh browser session.
  //
  // Guarded by a short cooldown so rapid focus/blur (e.g. keyboard opening)
  // doesn't hammer the relays.
  const lastForegroundRefetch = useRef(0);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      // Only bother if we've been away long enough for sockets to have died.
      if (now - lastForegroundRefetch.current < 15_000) return;
      lastForegroundRefetch.current = now;

      // 1. Re-establish relay connections the OS tore down while suspended.
      reconnectRelays();

      // 2. Give the sockets a moment to open, then refetch the visible feed
      //    and boosts. Cascade the boost refetch to absorb propagation delay.
      //    Refetch both feed queries — whichever is active gets fresh data,
      //    and the inactive one is a cheap no-op (its subscription is idle).
      setTimeout(() => {
        refetchCommunity();
        refetchFollow();
        refetchBoosts();
      }, 400);
      setTimeout(refetchBoosts, 2500);
    };

    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [refetchCommunity, refetchFollow, refetchBoosts]);

  // Fetch boosted post events by ID so they always appear regardless of hashtags/age
  const boostedPostIds = useMemo(() => [...activeBoostMap.keys()], [activeBoostMap]);
  // Resolve boosted posts by ID across ALL boostable kinds. A boost is keyed
  // only by event id, but any post can be boosted — a plain note (1), a poll
  // (1068), a NIP-72 COMMUNITY POST (1111), or a Cash Clip (22). Previously
  // this fetched only [1, 1068], so a boosted community post was never found
  // by the feed and silently never appeared even though it was paid for
  // ("my boosted post didn't appear — check the Community Group instead").
  // Fetching by id is safe regardless of kind; PostCard renders them all.
  const { events: fetchedBoostedPosts } = useNostrQuery({
    filters: boostedPostIds.length > 0 ? [{ kinds: [1, 1068, 1111, 22], ids: boostedPostIds, limit: 30 }] : [],
    enabled: boostedPostIds.length > 0,
    allRelays: !_dataSaver,
  });

  // Fetch boosted campaign events — use two strategies:
  // 1. Direct ID lookup for campaigns referenced by active boosts
  // 2. Broad tag query (no since filter) so old campaigns that are newly boosted still appear
  // Query all relays so we don't miss campaigns stored on different relays
  const { events: boostedCampaignEvents } = useNostrQuery({
    filters: boostedPostIds.length > 0 ? [
      { kinds: [CAMPAIGN_KIND], ids: boostedPostIds, limit: 20 },
      ...(_dataSaver ? [] : [{ kinds: [CAMPAIGN_KIND], '#t': [CAMPAIGN_TAG], limit: 100 }]),
    ] : [],
    enabled: boostedPostIds.length > 0,
    allRelays: !_dataSaver,
  });

  // Parse boosted campaigns — match against active boost map
  const boostedCampaigns = useMemo(() => {
    return boostedCampaignEvents
      .map(ev => {
        try {
          const data: CampaignData = JSON.parse(ev.content);
          if (!data.title || !data.goalSats) return null;
          const boostData = activeBoostMap.get(ev.id);
          if (!boostData) return null;
          return { event: ev, data, id: ev.id, boostData };
        } catch { return null; }
      })
      .filter(Boolean) as { event: any; data: CampaignData; id: string; boostData: BoostData }[];
  }, [boostedCampaignEvents, activeBoostMap]);

  // Sponsored business ads — verified businesses only, on-chain confirmed.
  // Woven into the community feed at BUSINESS_AD_SLOTS. Fewer in data-saver.
  const { ads: businessAds } = useBusinessAds(_dataSaver ? 1 : 3);

  // ── Trending: bulk engagement query ─────────────────────────────
  // Fetch reactions, reposts, replies, and tips for all recent community posts
  // in a single bulk query. Only active when trending tab is selected.
  const trendingPostIds = useMemo(() => {
    if (feedType !== 'trending') return [];
    const now = Math.floor(Date.now() / 1000);
    return communityEvents
      .filter(e => (now - e.created_at) <= 48 * 3600) // 48h cutoff
      .map(e => e.id)
      .slice(0, 100); // Cap to avoid oversized queries
  }, [communityEvents, feedType]);

  const { events: trendingEngagement } = useNostrQuery({
    filters: trendingPostIds.length > 0 ? [
      { kinds: [7, 6, 1], '#e': trendingPostIds, limit: _dataSaver ? 100 : 500, since: trendingSince },
    ] : [],
    enabled: feedType === 'trending' && trendingPostIds.length > 0,
    allRelays: !_dataSaver,
  });

  const { events: trendingTips } = useNostrQuery({
    filters: trendingPostIds.length > 0 ? [
      { kinds: [9735], '#e': trendingPostIds, limit: _dataSaver ? 50 : 200, since: trendingSince },
    ] : [],
    enabled: feedType === 'trending' && trendingPostIds.length > 0,
    allRelays: !_dataSaver,
  });

  // Score and rank posts for trending
  const trendingScoreMap = useMemo(() => {
    if (feedType !== 'trending') return new Map<string, TrendingScore>();

    // Group engagement by event ID
    const engagementByPost = new Map<string, import('nostr-tools').Event[]>();
    const tipsByPost = new Map<string, import('nostr-tools').Event[]>();

    for (const ev of trendingEngagement) {
      const refId = (ev.tags || []).find(t => t[0] === 'e')?.[1];
      if (refId) {
        const arr = engagementByPost.get(refId) || [];
        arr.push(ev);
        engagementByPost.set(refId, arr);
      }
    }
    for (const ev of trendingTips) {
      const refId = (ev.tags || []).find(t => t[0] === 'e')?.[1];
      if (refId) {
        const arr = tipsByPost.get(refId) || [];
        arr.push(ev);
        tipsByPost.set(refId, arr);
      }
    }

    // Calculate scores
    const scoreMap = new Map<string, TrendingScore>();
    for (const post of communityEvents) {
      if (!trendingPostIds.includes(post.id)) continue;

      const score = calculateTrendingScore({
        reactions: engagementByPost.get(post.id) || [],
        tipReceipts: tipsByPost.get(post.id) || [],
        isBoosted: activeBoostedIds.has(post.id),
        authorPubkey: post.pubkey,
        createdAt: post.created_at,
      });

      if (score) {
        score.eventId = post.id;
        scoreMap.set(post.id, score);
      }
    }

    return scoreMap;
  }, [feedType, communityEvents, trendingEngagement, trendingTips, trendingPostIds, activeBoostedIds]);

  // ── Verified feed: trigger NIP-05 verification for authors ──────
  // When verified tab is active, batch-check all unique post authors.
  // Use a counter to force re-render when verifications complete.
  const [verifiedCheckCounter, setVerifiedCheckCounter] = useState(0);
  useEffect(() => {
    if (feedType !== 'verified' || communityEvents.length === 0) return;
    const pubkeys = [...new Set(communityEvents.map(e => e.pubkey))];
    triggerVerificationForPubkeys(pubkeys, getCachedProfile).then(() => {
      setVerifiedCheckCounter(c => c + 1);
    });
  }, [feedType, communityEvents.length]);

  const loading = (feedType === 'community' || feedType === 'trending' || feedType === 'verified') ? communityLoading : followLoading;
  const refetch = (feedType === 'community' || feedType === 'trending' || feedType === 'verified') ? refetchCommunity : refetchFollow;

  // Force re-render when new posts are published
  const [publishCount, setPublishCount] = useState(0);
  const handlePublished = useCallback(() => {
    setPublishCount(c => c + 1);
    // Aggressive refetch cascade to catch relay propagation
    const doRefetch = () => { refetchCommunity(); if (follows.length > 0) refetchFollow(); };
    setTimeout(doRefetch, 2000);
    setTimeout(doRefetch, 5000);
    setTimeout(doRefetch, 10000);
    setTimeout(doRefetch, 20000);
  }, [refetchCommunity, refetchFollow, follows.length]);

  // ── New posts banner ─────────────────────────────────────────────
  // Track post IDs seen on first load, then count anything new that arrives
  const [seenIds, setSeenIds] = useState<Set<string>>(() => new Set());
  const [newPostCount, setNewPostCount] = useState(0);
  const initialLoadDone = useRef(false);

  // Once the feed has loaded its first batch, snapshot the IDs
  useEffect(() => {
    if (initialLoadDone.current) return;
    const events = communityEvents ?? [];
    if (!loading && events.length > 0) {
      setSeenIds(new Set(events.map(e => e.id)));
      initialLoadDone.current = true;
    }
  }, [loading, communityEvents]);

  // After initial load, watch for genuinely new events.
  // Only update state when the count actually changes to avoid
  // unnecessary re-renders of the entire feed.
  // IMPORTANT: Edits (events with '-edit-of' tag) are NOT counted as new posts.
  // The user's edit replaces the original — it shouldn't trigger "new posts" banner.
  const prevNewCountRef = useRef(0);
  useEffect(() => {
    if (!initialLoadDone.current || seenIds.size === 0) return;
    const events = communityEvents ?? [];
    const newCount = events.filter(e => {
      if (seenIds.has(e.id)) return false;
      if (isSpamContent(e.content)) return false;
      if (isSpamAccount(e.pubkey)) return false;
      // Don't count edits as new posts — they replace an existing post
      if (e.tags?.some((t: string[]) => t[0] === '-edit-of')) return false;
      return true;
    }).length;
    if (newCount !== prevNewCountRef.current) {
      prevNewCountRef.current = newCount;
      setNewPostCount(newCount);
    }
  }, [communityEvents, seenIds]);

  const handleShowNewPosts = useCallback(() => {
    const events = communityEvents ?? [];
    setSeenIds(new Set(events.map(e => e.id)));
    setNewPostCount(0);
    setVisibleCount(POSTS_PER_PAGE);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [communityEvents]);

  const allFilteredEvents = useMemo(() => {
    const relayEvents = (feedType === 'community' || feedType === 'trending' || feedType === 'verified') ? communityEvents : followEvents;

    // Only inject locally-cached posts that actually belong in this feed.
    // For the Following feed, restrict to posts authored by people you follow
    // (or yourself if you're in that list). Never inject your own posts into
    // someone else's feed — that's what was causing your posts to appear in Following.
    const localPosts = getLocalEvents().filter(e => {
      if (e.kind !== 1 && e.kind !== 1068) return false;
      if (feedType === 'community' || feedType === 'trending') return true;
      if (feedType === 'verified') return isVerifiedPubkeyCached(e.pubkey, getCachedProfile);
      // Following feed: only show posts from authors in the follows list
      return follows.includes(e.pubkey);
    });

    const all = [...localPosts, ...relayEvents];

    // ── Collect IDs of original posts that have been replaced by edits ──
    // When a user edits a post, the new version includes a '-edit-of' tag
    // pointing to the original event ID. We collect those so the original
    // can be hidden from the feed (only the edited version should show).
    //
    // We also track the original's created_at so we can sort the edit at
    // the original's position (some signer extensions override created_at
    // with the current time, which would push the edit to the top as if
    // it were a brand new post — this fixes that).
    const editedOriginalIds = new Set<string>();
    const editOriginalTimestamps = new Map<string, number>(); // editEventId → original created_at
    for (const e of all) {
      const editOfTag = e.tags?.find((t: string[]) => t[0] === '-edit-of');
      if (editOfTag?.[1]) {
        editedOriginalIds.add(editOfTag[1]);
        // Find the original event and record its timestamp
        const original = all.find(o => o.id === editOfTag[1]);
        if (original) {
          editOriginalTimestamps.set(e.id, original.created_at);
        }
      }
    }

    // Sorting helper: for edits, use the original post's timestamp so they
    // stay in the original position instead of floating to the top.
    const sortTimestamp = (e: any): number => {
      return editOriginalTimestamps.get(e.id) ?? e.created_at;
    };

    const seen = new Set<string>();
    return all
      .sort((a, b) => sortTimestamp(b) - sortTimestamp(a))
      .filter(e => {
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        if (isMuted(e.pubkey)) return false;
        if (isBanned(e.pubkey)) return false;
        if (isSpamContent(e.content)) return false;
        if (isSpamAccount(e.pubkey)) return false;
        // Hide original posts that have been replaced by an edit
        if (editedOriginalIds.has(e.id)) return false;
        // Filter out utility kind-1 events that aren't real feed posts.
        // These are published from BCH Nostr (so they now match the
        // client:BCHNostr filter) but belong to their own feature surfaces:
        //   - chess game events (challenges, moves, accepts, resigns)
        //   - poker game events
        //   - wordle score shares
        //   - in-app feedback / bug reports
        const UTILITY_KIND1_TAGS = new Set([
          'bchnostr-chess',
          'bchnostr-poker',
          'bchnostr-wordle',
          'bchnostr-feedback',
        ]);
        if (e.tags?.some((t: string[]) => t[0] === 'chess' || (t[0] === 't' && UTILITY_KIND1_TAGS.has(t[1])))) return false;
        // Filter out replies — kind 1 events that reference another event
        // via an 'e' tag with a 'reply' or unmarked root marker are replies.
        // Exceptions:
        //   - Quote-reposts have a 'q' tag AND may have an 'e' tag
        //   - Edited posts only have 'edit' marker e-tags (or '-edit-of' tag)
        //     — these are new versions of top-level posts, NOT replies.
        //   - Posts with 'mention' marker e-tags are NOT replies (just referencing)
        //   - Posts with 'a' tags are NOT necessarily replies — they may reference
        //     communities, articles, or other addressable content.
        // Never show kind 1111 (NIP-22 comments) in the feed — they belong in their parent context
        if (e.kind === 1111) return false;
        // Filter out calendar event comments (tagged 'bchevent') — they belong in the event feed
        if (e.kind === 1 && e.tags?.some((t: string[]) => t[0] === 't' && t[1] === 'bchevent')) return false;
        if (e.kind === 1) {
          const eTags = e.tags?.filter((t: string[]) => t[0] === 'e') || [];
          const isQuote = e.tags?.some((t: string[]) => t[0] === 'q');
          // Check for e-tags that indicate this is a reply (not just a mention or edit marker).
          // A reply has an e-tag with marker 'reply', 'root', or NO marker at all (legacy NIP-10).
          // 'mention' and 'edit' markers are NOT reply indicators.
          const hasReplyETag = eTags.some((t: string[]) => {
            const marker = t[3];
            // If marker is 'edit' or 'mention', this e-tag is not a reply signal
            if (marker === 'edit' || marker === 'mention') return false;
            // 'reply' or 'root' markers indicate a reply
            if (marker === 'reply' || marker === 'root') return true;
            // No marker (legacy NIP-10) — treat as a reply signal only if there's
            // no other tag context suggesting it's a top-level post
            if (!marker || marker === '') return true;
            return false;
          });
          if (hasReplyETag && !isQuote) return false;
          // Filter out legacy kind 1 comments on articles/addressable content.
          // These have an 'a' tag with a 'root' marker (e.g. blog comments) but
          // no 'e' reply tags — they slipped through the e-tag check above.
          const hasARootTag = e.tags?.some((t: string[]) => t[0] === 'a' && t[3] === 'root');
          if (hasARootTag && !isQuote) return false;
        }
        // Verified feed: only show posts from verified authors
        if (feedType === 'verified' && !isVerifiedPubkeyCached(e.pubkey, getCachedProfile)) return false;
        return true;
      });
  // Use stable references: both event arrays as deps so React tracks them properly.
  // The conditional selection happens inside the memo body, not in the dep array.
  }, [communityEvents, followEvents, feedType, publishCount, verifiedCheckCounter]);

  // Separate boosted posts from regular posts, then weave boosted posts
  // into the regular feed at bid-ranked positions.
  // Highest bid → best slot (position 2), next highest → position 5, etc.
  const { mixedFeed, regularPosts } = useMemo(() => {
    // Build a map of original event ID → latest edit event.
    // When a boosted post has been edited, the boost still references the
    // original event ID, but we want to display the edited content.
    const editMap = new Map<string, any>(); // originalId → edit event
    const allSourceEvents = [...(communityEvents ?? []), ...(followEvents ?? [])];
    for (const e of allSourceEvents) {
      const editOfTag = e.tags?.find((t: string[]) => t[0] === '-edit-of');
      if (editOfTag?.[1]) {
        const existing = editMap.get(editOfTag[1]);
        if (!existing || e.created_at > existing.created_at) {
          editMap.set(editOfTag[1], e);
        }
      }
    }

    // Collect boosted posts and sort by bid amount (highest first).
    // If a boosted post has been edited, swap in the edited content
    // while preserving the original event's ID (so boost references match).
    const seen = new Set<string>();
    const boostedEvents: any[] = [];
    const resolveBoostedEvent = (e: any) => {
      const editVersion = editMap.get(e.id);
      if (editVersion) {
        // Create a synthetic event with the original ID but edited content
        return { ...e, content: editVersion.content, tags: editVersion.tags, _edited: true };
      }
      return e;
    };
    fetchedBoostedPosts
      .filter(e => activeBoostedIds.has(e.id) && !seen.has(e.id))
      .forEach(e => { seen.add(e.id); boostedEvents.push(resolveBoostedEvent(e)); });
    allFilteredEvents
      .filter(e => activeBoostedIds.has(e.id) && !seen.has(e.id))
      .forEach(e => { seen.add(e.id); boostedEvents.push(resolveBoostedEvent(e)); });

    // Get BoostData for each event and sort by bid amount
    const boostedWithData = boostedEvents
      .map(e => ({ event: e, data: activeBoostMap.get(e.id) }))
      .filter(b => b.data)
      .sort((a, b) => (b.data!.priceSats) - (a.data!.priceSats));

    // Cap to max visible boost slots
    const visibleBoosted = boostedWithData.slice(0, BOOST_MAX_VISIBLE);

    // Log for debugging boost weaving
    if (activeBoostedIds.size > 0) {
      const fromFetch = fetchedBoostedPosts.filter(e => activeBoostedIds.has(e.id)).length;
      const fromFeed = allFilteredEvents.filter(e => activeBoostedIds.has(e.id)).length;
      console.log(
        `[Boost] Weaving ${visibleBoosted.length} boosted posts into feed ` +
        `(${fromFetch} from direct fetch, ${fromFeed} from feed, ${boostedEvents.length} total resolved)`
      );
    }

    // Regular posts — exclude anything boosted
    const regular = allFilteredEvents.filter(e => !activeBoostedIds.has(e.id));

    // Weave boosted posts into feed at designated slot positions.
    // BOOST_SLOT_POSITIONS = [2, 5, 9, 14, 20] — 1-indexed feed positions
    // Highest bidder gets slot 2, next gets slot 5, etc.
    // If there aren't enough regular posts to reach a slot position,
    // the boost is appended at the end so it still appears.
    const mixed: { event: any; isBoosted: boolean }[] = [];
    let regularIdx = 0;
    let boostSlotIdx = 0;

    for (let feedPos = 0; regularIdx < regular.length || boostSlotIdx < visibleBoosted.length; feedPos++) {
      // Check if a boosted post should be inserted at this position
      if (
        boostSlotIdx < visibleBoosted.length &&
        boostSlotIdx < BOOST_SLOT_POSITIONS.length &&
        feedPos === BOOST_SLOT_POSITIONS[boostSlotIdx] - 1 // convert 1-indexed to 0-indexed
      ) {
        mixed.push({ event: visibleBoosted[boostSlotIdx].event, isBoosted: true });
        boostSlotIdx++;
      } else if (regularIdx < regular.length) {
        mixed.push({ event: regular[regularIdx], isBoosted: false });
        regularIdx++;
      } else if (boostSlotIdx < visibleBoosted.length) {
        // No more regular posts but still have boosts — append them
        // so they always show even if the feed is short.
        mixed.push({ event: visibleBoosted[boostSlotIdx].event, isBoosted: true });
        boostSlotIdx++;
      } else {
        break;
      }
    }

    // Any remaining regular posts
    while (regularIdx < regular.length) {
      mixed.push({ event: regular[regularIdx++], isBoosted: false });
    }

    return { mixedFeed: mixed, regularPosts: regular };
  }, [allFilteredEvents, activeBoostedIds, fetchedBoostedPosts, activeBoostMap, communityEvents, followEvents]);

  // Trending feed: filter to scored posts, sort by trending score
  const trendingEvents = useMemo(() => {
    if (feedType !== 'trending') return [];
    return allFilteredEvents
      .filter(e => trendingScoreMap.has(e.id))
      .sort((a, b) => {
        const sa = trendingScoreMap.get(a.id)?.finalScore ?? 0;
        const sb = trendingScoreMap.get(b.id)?.finalScore ?? 0;
        return sb - sa;
      });
  }, [feedType, allFilteredEvents, trendingScoreMap]);

  // Active feed events (depends on feed type)
  // Community feed uses mixedFeed (boosted posts woven in every 5th slot)
  // Verified feed uses the same ranking as community but filtered to verified authors
  const activeFeedItems = feedType === 'trending'
    ? trendingEvents.map(e => ({ event: e, isBoosted: false }))
    : feedType === 'verified'
      ? allFilteredEvents.map(e => ({ event: e, isBoosted: false }))
      : mixedFeed;
  const filteredItems = activeFeedItems.slice(0, visibleCount);
  const hasMore = activeFeedItems.length > visibleCount;

  return (
    <div className="feed-scroll-anchor">
      {/* Feed Toggle */}
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border feed-widget-no-anchor">
        <div className="flex">
          <button
            onClick={() => setFeedType('community')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
              feedType === 'community'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Globe className="w-4 h-4" /> <span className="hidden sm:inline">Community</span><span className="sm:hidden">Latest</span>
          </button>
          <button
            onClick={() => setFeedType('trending')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
              feedType === 'trending'
                ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Flame className={`w-4 h-4 ${feedType === 'trending' ? 'text-orange-500' : ''}`} /> Trending
          </button>
          <button
            onClick={() => setFeedType('following')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
              feedType === 'following'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Users className="w-4 h-4" /> <span className="hidden xs:inline">Following</span>
          </button>
          <button
            onClick={() => setFeedType('verified')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-sm font-medium border-b-2 transition-colors ${
              feedType === 'verified'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <BadgeCheck className={`w-4 h-4 ${feedType === 'verified' ? 'text-emerald-500' : ''}`} /> <span className="hidden sm:inline">Verified</span>
          </button>
          <button
            onClick={refetch}
            className="px-3 py-3 text-muted-foreground hover:text-primary transition-colors"
            title="Refresh feed"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Above-feed widgets: marked as non-anchor targets so their async
           loading/resizing doesn't disrupt scroll position of posts below ── */}
      <div className="feed-widget-no-anchor">
        {/* Hero Splash — logged-out visitors only */}
        {!user && (
          <HeroSplash onLogin={() => login()} userCount={userCount} />
        )}

        {/* New posts banner */}
        {feedType !== 'trending' && feedType !== 'verified' && (
          <NewPostsBanner count={newPostCount} onShow={handleShowNewPosts} />
        )}

        {/* Live stream notification */}
        <LiveStreamBanner />

        {/* Live BCH Rooms (audio) notification */}
        <LiveRoomsBanner />

        {/* Composer */}
        <Composer onPublished={handlePublished} />

        {/* Scam & Bot Warning */}
        <ScamWarningBanner />

        {/* New Account Restrictions Banner */}
        {user && <NewAccountBanner pubkey={user.pubkey} />}

        {/* Enable Push Notifications CTA — big retention lever */}
        {user && <EnablePushBanner pubkey={user.pubkey} />}

        {/* Support Someone New */}
        <SupportSomeoneNew compact dismissable />

        {/* Build-your-own-site cross-promo (dismissible) */}
        <BCHLaunchCard />

        {/* Trending BCH tokens (Cauldron) */}
        <TrendingTokensCard />

        {/* BCH Radio Discovery Card */}
        <RadioDiscoveryCard />

        {/* Get Verified CTA — only for logged-in users without @bchnostr.com */}
        {user && <GetVerifiedBanner pubkey={user.pubkey} />}

        {/* Join Relay CTA — for users who haven't added relay.bchnostr.com */}
        {user && <JoinRelayBanner />}

        {/* Who to Follow — shown when user has few follows */}
        <WhoToFollowWidget myPubkey={user?.pubkey} follows={follows} communityEvents={communityEvents ?? []} />
      </div>

      {/* Feed */}
      {loading && filteredItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <BCHLogo size={40} />
          <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">
            {feedType === 'trending' ? 'Finding trending posts...' : feedType === 'verified' ? 'Loading verified feed...' : 'Loading feed...'}
          </p>
        </div>
      ) : filteredItems.length === 0 && boostedCampaigns.length === 0 ? (
        <div className="text-center py-16 px-4">
          <BCHLogo size={48} />
          <p className="text-muted-foreground mt-3 mb-2">
            {feedType === 'following'
              ? follows.length === 0
                ? "You're not following anyone yet."
                : 'No recent posts from people you follow.'
              : feedType === 'trending'
                ? 'Nothing trending right now.'
                : feedType === 'verified'
                  ? 'No verified posts yet. Get verified to appear here!'
                  : 'No posts yet. Be the first to post!'}
          </p>
          {feedType === 'following' ? (
            <div className="space-y-2">
              {follows.length === 0 && (
                <p className="text-xs text-muted-foreground">Follow some people to see their posts here.</p>
              )}
              <button
                onClick={() => setFeedType('community')}
                className="text-sm text-primary hover:underline"
              >
                Switch to Community Feed
              </button>
            </div>
          ) : feedType === 'trending' ? (
            <div className="max-w-xs mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 via-red-500 to-amber-500 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-orange-500/20">
                <Flame className="w-8 h-8 text-white" />
              </div>
              <p className="text-base font-bold mb-1">Nothing trending yet</p>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                When posts get replies, BCH tips, and reactions within 48 hours, they'll show up here ranked by community engagement.
              </p>
              <div className="flex items-center justify-center gap-2 mb-4 text-[10px] text-muted-foreground">
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted">💚 Tips</span>
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted">💬 Replies</span>
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted">❤\uFE0F Reactions</span>
              </div>
              <button
                onClick={() => setFeedType('community')}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Browse Community Feed
              </button>
            </div>
          ) : feedType === 'verified' ? (
            <div className="max-w-xs mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/20">
                <BadgeCheck className="w-8 h-8 text-white" />
              </div>
              <p className="text-base font-bold mb-1">Verified Feed</p>
              <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
                This feed only shows posts from users who've verified their identity with a <strong>$1 BCH</strong> payment. Get verified to appear here and enjoy a spam-free experience.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Link
                  to="/settings?tab=profile"
                  className="px-4 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors"
                >
                  Get Verified
                </Link>
                <button
                  onClick={() => setFeedType('community')}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
                >
                  Community Feed
                </button>
              </div>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              All posts from BCHNostr show up here automatically.
            </p>
          )}
        </div>
      ) : (
        <>
          {/* Trending feed: hero banner */}
          {feedType === 'trending' && filteredItems.length > 0 && (
            <div className="trending-hero trending-slide-in border-b border-primary/10">
              <div className="px-4 py-5 relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 via-red-500 to-amber-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
                    <Flame className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold flex items-center gap-1.5">Trending in BCH</h2>
                    <p className="text-[11px] text-muted-foreground">Community-curated by engagement, not algorithms</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1.5 text-[10px] font-medium px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                    <BCHLogo size={10} /> Powered by BCH
                  </span>
                  <span className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400">
                    <span className="text-xs">💬</span> Community supported
                  </span>
                  <span className="flex items-center gap-1 text-[10px] font-medium px-2 py-1 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400">
                    <span className="text-xs">⏱</span> Last 48 hours
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Boosted campaigns — injected after 2nd regular post */}
          {boostedCampaigns.length > 0 && feedType === 'community' && (
            <div className="space-y-3 py-3 px-2 sm:px-0 feed-widget-no-anchor">
              {boostedCampaigns.map(c => (
                <BoostedCampaignCard key={c.id} campaign={c} bchPrice={bchPrice} />
              ))}
            </div>
          )}

          {/* Now Playing card — shows occasionally in feed when Soundtrack mode is active */}
          <NowPlayingFeedCard insertAt={5} totalItems={filteredItems.length} />

          {/* Main feed — boosted posts woven in every 5th slot */}
          {filteredItems.map(({ event, isBoosted }, index) => {
            const trendingScore = feedType === 'trending' ? trendingScoreMap.get(event.id) : undefined;
            const topClass = feedType === 'trending'
              ? index === 0 ? 'trending-post-wrapper trending-post-top1'
              : index === 1 ? 'trending-post-wrapper trending-post-top2'
              : index === 2 ? 'trending-post-wrapper trending-post-top3'
              : 'trending-post-wrapper'
              : '';
            const slideStyle = feedType === 'trending'
              ? { animationDelay: `${Math.min(index * 60, 600)}ms` }
              : undefined;

            // A Sponsored business card is woven in after certain positions,
            // community feed only. Each active ad maps to one slot in order.
            const adSlotIdx = BUSINESS_AD_SLOTS.indexOf(index);
            const sponsoredAd =
              feedType === 'community' && adSlotIdx >= 0 && adSlotIdx < businessAds.length
                ? businessAds[adSlotIdx]
                : null;
            const sponsoredCard = sponsoredAd ? (
              <div
                key={`ad-${sponsoredAd.id}`}
                className="rounded-xl border border-border mx-2 my-3 sm:mx-0 overflow-hidden feed-post-anchor feed-post-contain"
              >
                <SponsoredBusinessCard ad={sponsoredAd} />
              </div>
            ) : null;

            if (isBoosted) {
              const boostData = activeBoostMap.get(event.id);
              const bidLabel = boostData
                ? (boostData.bidUsd ? `$${boostData.bidUsd.toFixed(2)}` : satsToUSD(boostData.priceSats, bchPrice))
                : null;
              return (
                <React.Fragment key={event.id}>
                  <div
                    className="relative rounded-xl border-2 mx-2 my-3 sm:mx-0 animate-boost-border animate-boost-glow boost-shimmer feed-post-anchor feed-post-contain"
                  >
                    <div className="flex items-center justify-between gap-2 px-3 py-1.5 rounded-t-[10px] boost-badge">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Rocket className="w-3.5 h-3.5 text-amber-950 shrink-0" />
                        <span className="text-[11px] font-extrabold text-amber-950 uppercase tracking-wider shrink-0">Promoted</span>
                        {bidLabel && (
                          <span className="text-[10px] font-bold text-amber-900/80 truncate">{bidLabel} bid</span>
                        )}
                      </div>
                      <BoostCountdown expiresAt={boostData?.expiresAt} on="gold" />
                    </div>
                    <PostCard event={event} />
                  </div>
                  {sponsoredCard}
                </React.Fragment>
              );
            }

            return (
              <React.Fragment key={event.id}>
                <div
                  className={`${topClass} ${feedType === 'trending' ? 'trending-slide-in' : ''} feed-post-anchor feed-post-contain`}
                  style={slideStyle}
                >
                  {trendingScore && (
                    <TrendingBadge
                      score={trendingScore}
                      rank={index + 1}
                      maxScore={trendingScoreMap.get(filteredItems[0]?.event.id)?.finalScore ?? 1}
                    />
                  )}
                  <PostCard event={event} />
                </div>
                {sponsoredCard}
              </React.Fragment>
            );
          })}
          {hasMore && (
            <div className="text-center py-6">
              <button
                onClick={handleLoadMore}
                className="px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
              >
                Load More ({activeFeedItems.length - visibleCount} remaining)
              </button>
            </div>
          )}
          {!hasMore && filteredItems.length > 0 && (
            <div className="text-center py-8">
              {feedType === 'trending' ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
                    <Flame className="w-3.5 h-3.5 text-orange-500" />
                    {filteredItems.length} trending post{filteredItems.length !== 1 ? 's' : ''} from the last 48h
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-px flex-1 max-w-[60px] bg-gradient-to-r from-transparent to-orange-500/20" />
                    <p className="text-[10px] text-muted-foreground/50 flex items-center gap-1.5 font-medium">
                      <BCHLogo size={11} /> Powered by BCH community engagement
                    </p>
                    <div className="h-px flex-1 max-w-[60px] bg-gradient-to-l from-transparent to-primary/20" />
                  </div>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">You've seen all {filteredItems.length} posts</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Animated Rank Counter ────────────────────────────────────────
// Counts up from 0 to the target rank with a smooth entrance animation

function AnimatedRank({ rank }: { rank: number }) {
  const [displayRank, setDisplayRank] = useState(0);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const mountTimer = setTimeout(() => setMounted(true), 50);

    // Count-up animation: 0 → rank over ~400ms with eased steps
    if (rank <= 1) {
      // For rank 1, just pop in
      const t = setTimeout(() => setDisplayRank(rank), 150);
      return () => { clearTimeout(mountTimer); clearTimeout(t); };
    }

    const steps = Math.min(rank, 12); // Cap steps for large numbers
    const duration = 400;
    const stepDelay = duration / steps;

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= steps; i++) {
      // Ease-out: faster at start, slower at end
      const progress = i / steps;
      const eased = 1 - Math.pow(1 - progress, 2);
      const value = Math.round(eased * rank);
      timers.push(setTimeout(() => setDisplayRank(value), stepDelay * i));
    }
    // Ensure final value is exact
    timers.push(setTimeout(() => setDisplayRank(rank), duration + 50));

    return () => {
      clearTimeout(mountTimer);
      timers.forEach(clearTimeout);
    };
  }, [rank]);

  return (
    <span className={`inline-flex transition-all duration-300 ${mounted ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}>
      #{displayRank}
    </span>
  );
}

// ── "Hot Right Now" Pulse Dot ───────────────────────────────────
// Shown when a post gained engagement in the last hour

function HotPulseDot() {
  return (
    <span className="relative inline-flex items-center gap-1 ml-1" title="Active in the last hour">
      <span className="relative flex h-2 w-2">
        <span className="hot-pulse-ring absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-500" />
      </span>
      <span className="text-[9px] font-bold text-orange-500 dark:text-orange-400 uppercase tracking-wider">Live</span>
    </span>
  );
}

// ── Trending Badge (shown above posts in trending feed) ─────────

const TrendingBadge = React.memo(function TrendingBadge({ score, rank, maxScore }: { score: TrendingScore; rank: number; maxScore: number }) {
  const emoji = getTrendingReasonEmoji(score.reason);
  const label = getTrendingReasonLabel(score);
  const barPct = maxScore > 0 ? Math.max(8, Math.round((score.finalScore / maxScore) * 100)) : 0;
  const isHot = score.recentEngagementCount >= 2;

  // Build detail chips showing WHY this is trending
  const chips: { label: string; icon: string; color: string }[] = [];
  if (score.uniqueTippers > 0) chips.push({
    label: `${score.uniqueTippers}`,
    icon: '💚',
    color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  });
  if (score.replyCount > 0) chips.push({
    label: `${score.replyCount}`,
    icon: '💬',
    color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  });
  if (score.repostCount > 0) chips.push({
    label: `${score.repostCount}`,
    icon: '\uD83D\uDD01',
    color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  });
  if (score.reactionCount > 0) chips.push({
    label: `${score.reactionCount}`,
    icon: '❤\uFE0F',
    color: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  });

  return (
    <div className="px-4 pt-3 pb-1 space-y-1.5">
      {/* Top row: rank + reason + hot indicator + chips */}
      <div className="flex items-center gap-2">
        {/* Rank badge with count-up animation */}
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-extrabold shrink-0 ${
          rank === 1 ? 'bg-gradient-to-br from-amber-400 via-yellow-400 to-orange-500 text-white shadow-md shadow-amber-500/30 trending-rank-1'
          : rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white dark:from-slate-500 dark:to-slate-600 shadow-sm'
          : rank === 3 ? 'bg-gradient-to-br from-amber-600 to-amber-800 text-amber-100 shadow-sm'
          : 'bg-muted text-muted-foreground'
        }`}>
          <AnimatedRank rank={rank} />
        </div>
        {/* Reason label */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-base leading-none">{emoji}</span>
          <span className="text-[11px] font-bold text-foreground truncate">{label}</span>
          {isHot && <HotPulseDot />}
        </div>
        {/* Compact engagement chips */}
        <div className="flex items-center gap-1 ml-auto shrink-0">
          {chips.map(chip => (
            <span key={chip.icon} className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${chip.color}`} title={chip.icon === '💚' ? 'Tippers' : chip.icon === '💬' ? 'Replies' : chip.icon === '\uD83D\uDD01' ? 'Reposts' : 'Reactions'}>
              <span className="text-[9px]">{chip.icon}</span>
              {chip.label}
            </span>
          ))}
        </div>
      </div>
      {/* Score bar — visual representation of trending strength */}
      <div className="h-1 rounded-full bg-muted/60 overflow-hidden">
        <div
          className="trending-score-bar h-full rounded-full"
          style={{
            width: `${barPct}%`,
            background: rank <= 3
              ? 'linear-gradient(90deg, #f97316, #fbbf24, #0AC18E)'
              : 'linear-gradient(90deg, hsl(var(--primary) / 0.6), hsl(var(--primary) / 0.3))',
          }}
        />
      </div>
    </div>
  );
});

// ── Boosted Campaign Card (shown in main feed) ──────────────────

function BoostedCampaignCard({ campaign, bchPrice }: {
  campaign: { event: any; data: CampaignData; id: string; boostData: BoostData };
  bchPrice: number | null;
}) {
  const { profile } = useNostrProfile(campaign.event.pubkey);
  const { data, boostData } = campaign;

  // Fetch pledges for this campaign to show progress
  const { events: pledges } = useNostrQuery({
    filters: [{ kinds: [9735], '#e': [campaign.id], limit: 200 }],
    enabled: true,
  });

  const raisedSats = pledges.reduce((sum, ev) => {
    const amountTag = (ev.tags || []).find((t: string[]) => t[0] === 'amount');
    return sum + (amountTag ? parseInt(amountTag[1]) || 0 : 0);
  }, 0);

  const goalUsd = satsToUSD(data.goalSats, bchPrice);
  const raisedUsd = satsToUSD(raisedSats, bchPrice);
  const pct = data.goalSats > 0 ? Math.min(100, Math.round((raisedSats / data.goalSats) * 100)) : 0;
  const daysLeft = Math.max(0, Math.ceil((data.deadline - Date.now() / 1000) / 86400));
  const isExpired = data.deadline < Date.now() / 1000;
  const isFunded = raisedSats >= data.goalSats;
  const displayName = profile?.display_name || profile?.name || campaign.event.pubkey.slice(0, 8);

  return (
    <div className="relative border-l-[3px] border-boost-gold">
      {/* Slim inline label */}
      <div className="flex items-center gap-1.5 px-4 pt-2">
        <Rocket className="w-3 h-3 text-boost-gold-ink shrink-0" />
        <span className="text-[11px] text-boost-gold-ink font-bold tracking-wide">Promoted Campaign</span>
        <BoostCountdown expiresAt={boostData.expiresAt} on="plain" />
      </div>

      {/* Campaign content */}
      <Link to={`/crowdfund?campaign=${campaign.id}`} className="block px-4 py-3 hover:bg-muted/30 transition-colors">
        {/* Campaign image */}
        {data.image && (
          <div className="rounded-lg overflow-hidden mb-3 max-h-40 -mx-1">
            <img src={data.image} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLElement).style.display = 'none'; }} />
          </div>
        )}

        <div className="flex gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Target className="w-5 h-5 text-primary" />
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-sm leading-snug mb-1">{data.title}</h3>

            {data.description && (
              <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{data.description}</p>
            )}

            {/* Progress bar */}
            <div className="h-1.5 rounded-full bg-muted overflow-hidden mb-2">
              <div
                className={`h-full rounded-full transition-all ${isFunded ? 'bg-emerald-500' : 'bg-primary'}`}
                style={{ width: `${pct}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-[11px] text-muted-foreground mb-1.5">
              <span className="font-medium">
                <span className="text-primary font-bold">{raisedUsd || `${raisedSats.toLocaleString()} sats`}</span>
                {' '}of {goalUsd || `${data.goalSats.toLocaleString()} sats`}
                {' '}({pct}%)
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {isExpired ? 'Ended' : isFunded ? 'Funded!' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left`}
              </span>
            </div>

            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1.5">
                {profile?.picture ? (
                  <img src={profile.picture} alt="" className="w-4 h-4 rounded-full object-cover" />
                ) : (
                  <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-2.5 h-2.5 text-primary/60" />
                  </div>
                )}
                <span className="text-[10px] text-muted-foreground">by {displayName}</span>
              </div>
              {pledges.length > 0 && (
                <span className="text-[10px] text-muted-foreground">{pledges.length} backer{pledges.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
        </div>
      </Link>

      {/* CTA footer */}
      {!isExpired && !isFunded && (
        <Link
          to={`/crowdfund?campaign=${campaign.id}`}
          className="flex items-center justify-center gap-2 mx-3 mb-3 py-2.5 rounded-xl bg-gradient-to-r from-primary to-emerald-500 text-white text-xs font-bold hover:opacity-90 transition-opacity"
        >
          <Heart className="w-3.5 h-3.5" />
          Back this campaign with BCH
        </Link>
      )}
    </div>
  );
}

// ── Radio Card Tip Proof ──────────────────────────────────────────
// Shows how much BCH a track has earned, displayed on the Radio Discovery Card.

function RadioCardTipProof({ trackId }: { trackId: string }) {
  const radio = useRadioPlayer();
  const tipSats = radio.tipCounts.get(trackId) || 0;
  const likeCount = radio.likeCounts.get(trackId) || 0;

  if (tipSats <= 0 && likeCount <= 0) return null;

  const bchPrice = getBCHPrice();
  const bch = tipSats / 1e8;
  const usd = bchPrice && bchPrice > 0 ? bch * bchPrice : 0;

  return (
    <div className="flex items-center gap-2 mt-1">
      {tipSats > 0 && (
        <span
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[10px] font-bold text-emerald-500"
          title={`${tipSats.toLocaleString()} sats earned in BCH tips${usd >= 0.01 ? ` (~$${usd.toFixed(2)})` : ''}`}
        >
          <Coins className="w-2.5 h-2.5" />
          {bch >= 0.01 ? `${bch.toFixed(4)} BCH` : bch >= 0.001 ? `${bch.toFixed(6)} BCH` : `${tipSats.toLocaleString()} sats`}
          {usd >= 0.01 && (
            <span className="text-emerald-400/70 font-medium ml-0.5">(${usd.toFixed(2)})</span>
          )}
        </span>
      )}
      {likeCount > 0 && (
        <span className="inline-flex items-center gap-0.5 text-[10px] text-red-500">
          <Heart className="w-2.5 h-2.5" /> {likeCount}
        </span>
      )}
    </div>
  );
}

// ── BCH Radio Discovery Card ─────────────────────────────────────
// A compact card on the Home page that features a rotating track from
// BCH Radio, encouraging music discovery and BCH tipping.
// Rotates the featured song every hour (based on timestamp hash).

const RadioDiscoveryCard = React.memo(function RadioDiscoveryCard() {
  const radio = useRadioPlayer();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('bchnostr:radio-card-dismissed') === '1'; } catch { return false; }
  });

  // Pick a featured track — rotates every hour using a stable index
  const featuredTrack = useMemo(() => {
    if (radio.queue.length === 0) return null;
    // Use the current hour as a seed so all users see rotation at the same cadence
    const hourSeed = Math.floor(Date.now() / (60 * 60 * 1000));
    const idx = hourSeed % radio.queue.length;
    return radio.queue[idx];
  }, [radio.queue]);

  if (dismissed || !featuredTrack || !radio.queueReady) return null;

  const isThisPlaying = radio.currentTrack?.id === featuredTrack.id && radio.isPlaying;

  return (
    <div className="border-b border-border">
      <div className="px-4 py-3">
        <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-primary/[0.03]">
          {/* Header */}
          <div className="flex items-center justify-between px-3.5 pt-3 pb-1">
            <div className="flex items-center gap-1.5">
              <Music className="w-3.5 h-3.5 text-primary" />
              <span className="text-[11px] font-bold text-primary tracking-wide">NOW ON BCH RADIO</span>
            </div>
            <button
              onClick={() => { setDismissed(true); try { sessionStorage.setItem('bchnostr:radio-card-dismissed', '1'); } catch {} }}
              className="p-0.5 rounded-full hover:bg-muted text-muted-foreground/50 hover:text-muted-foreground transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Track */}
          <div className="flex items-center gap-3 px-3.5 pb-3 pt-1">
            {/* Cover art */}
            <Link to={`/radio?track=${featuredTrack.id}`} className="shrink-0">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-border/60 flex items-center justify-center overflow-hidden shadow-sm">
                {featuredTrack.coverArt ? (
                  <img src={featuredTrack.coverArt} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <Disc3 className={`w-7 h-7 text-primary/40 ${isThisPlaying ? 'animate-spin' : ''}`} style={isThisPlaying ? { animationDuration: '3s' } : undefined} />
                )}
              </div>
            </Link>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <Link to={`/radio?track=${featuredTrack.id}`}>
                <p className="text-sm font-semibold truncate hover:text-primary transition-colors">{featuredTrack.title}</p>
              </Link>
              <p className="text-xs text-muted-foreground truncate">{featuredTrack.artist}</p>
              {/* Tip social proof */}
              <RadioCardTipProof trackId={featuredTrack.id} />
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => {
                  if (isThisPlaying) {
                    radio.togglePlay();
                  } else {
                    const idx = radio.queue.findIndex(t => t.id === featuredTrack.id);
                    if (idx >= 0) radio.playTrack(idx);
                  }
                }}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-90 ${
                  isThisPlaying
                    ? 'bg-primary/10 text-primary'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90'
                }`}
                title={isThisPlaying ? 'Pause' : 'Play'}
              >
                {isThisPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
              </button>
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex items-center gap-2 px-3.5 pb-3">
            <div className="flex-1" onClick={e => e.stopPropagation()}>
              <BCHTipButton recipientPubkey={featuredTrack.submittedBy} eventId={featuredTrack.id} />
            </div>
            <Link
              to="/radio"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"
            >
              <Radio className="w-3.5 h-3.5" />
              Open Radio
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
});

// ── Now Playing Feed Card ─────────────────────────────────────────
// A small card shown in the feed when Soundtrack My Feed is enabled
// and music is playing. Non-intrusive, appears after a few posts.

function NowPlayingFeedCard({ insertAt, totalItems }: { insertAt: number; totalItems: number }) {
  const radio = useRadioPlayer();

  // Only show if soundtrack mode is on, something is playing, and enough posts exist
  if (!radio.soundtrackMode || !radio.currentTrack || !radio.isPlaying || totalItems < insertAt) return null;

  const track = radio.currentTrack;
  const likeCount = radio.likeCounts.get(track.id) || 0;
  const tipSats = radio.tipCounts.get(track.id) || 0;
  const bchPrice = getBCHPrice();
  const tipBch = tipSats / 1e8;
  const tipUsd = bchPrice && bchPrice > 0 ? tipBch * bchPrice : 0;

  return (
    <div className="mx-3 my-3 sm:mx-0">
      <div className="rounded-xl border border-primary/20 bg-gradient-to-r from-primary/5 via-card to-card">
        <div className="flex items-center gap-3 p-3">
          {/* Cover art */}
          <Link to={`/radio?track=${track.id}`} className="shrink-0">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 border border-border flex items-center justify-center overflow-hidden">
              {track.coverArt ? (
                <img src={track.coverArt} alt="" className="w-full h-full object-cover" />
              ) : (
                <Disc3 className="w-7 h-7 text-primary/40 animate-spin" style={{ animationDuration: '3s' }} />
              )}
            </div>
          </Link>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Music className="w-3 h-3 text-primary" />
              <span className="text-[10px] font-bold text-primary uppercase tracking-wider">Now Playing</span>
            </div>
            <Link to={`/radio?track=${track.id}`}>
              <p className="text-sm font-semibold truncate hover:text-primary transition-colors">{track.title}</p>
            </Link>
            <p className="text-xs text-muted-foreground truncate">by {track.artist}</p>
            {/* Tip proof inline */}
            {tipSats > 0 && (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-[9px] font-bold text-emerald-500">
                  <Coins className="w-2.5 h-2.5" />
                  {tipBch >= 0.001 ? `${tipBch.toFixed(tipBch >= 0.01 ? 4 : 6)} BCH` : `${tipSats.toLocaleString()} sats`}
                  {tipUsd >= 0.01 && <span className="text-emerald-400/70 font-medium ml-0.5">(${tipUsd.toFixed(2)})</span>}
                  <span className="text-emerald-500/60 ml-0.5">earned</span>
                </span>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1.5 shrink-0">
            {likeCount > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] text-red-500">
                <Heart className="w-3 h-3" /> {likeCount}
              </span>
            )}
            <BCHTipButton recipientPubkey={track.submittedBy} eventId={track.id} />
            <button
              onClick={radio.togglePlay}
              className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 active:scale-95 transition-all"
            >
              {radio.isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


